var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { fetcher } from '@embed/store/fetchers';
function getFirstCDATASectionFromNodeList(nodeList) {
    return nodeList instanceof NodeList
        ? [...nodeList].find((e) => e instanceof CDATASection)
        : undefined;
}
function getFirstElementByTagName(doc, tagName) {
    return doc.getElementsByTagName(tagName)[0];
}
/**
 * Given a VAST XML document, extract a redirect URL.
 *
 * There are three places where we might find redirect URLs in a VAST tag.
 * The first is from the direct text content of the VASTAdTagURI tag.
 * The other two cases are Playlist tags and VMap tags, which rely on CDATASections.
 * [CDATASection](https://developer.mozilla.org/en-US/docs/Web/API/CDATASection) is like a text node.
 */
function getRedirectUrlFromVastXml(doc) {
    var _a, _b, _c, _d, _e, _f, _g;
    const redirectFromVastAdTag = (_a = getFirstElementByTagName(doc, 'VASTAdTagURI')) === null || _a === void 0 ? void 0 : _a.childNodes[0].textContent;
    if (redirectFromVastAdTag) {
        return redirectFromVastAdTag;
    }
    const redirectFromPlaylistAdTag = getFirstElementByTagName(doc, 'Playlist')
        ? (_d = (_c = getFirstCDATASectionFromNodeList((_b = getFirstElementByTagName(doc, 'Ad')) === null || _b === void 0 ? void 0 : _b.childNodes)) === null || _c === void 0 ? void 0 : _c.wholeText) === null || _d === void 0 ? void 0 : _d.trim()
        : undefined;
    if (redirectFromPlaylistAdTag) {
        return redirectFromPlaylistAdTag;
    }
    const redirectForVmapAdTag = (_g = (_f = getFirstCDATASectionFromNodeList((_e = getFirstElementByTagName(doc, 'vmap:AdTagURI')) === null || _e === void 0 ? void 0 : _e.childNodes)) === null || _f === void 0 ? void 0 : _f.wholeText) === null || _g === void 0 ? void 0 : _g.trim();
    if (redirectForVmapAdTag) {
        return redirectForVmapAdTag;
    }
    else {
        return undefined;
    }
}
/**
 * Test integration for DoubleVerify POC
 */
const modifyDoubleVerifyUrl = (urlObject, timestamp, pageUrl) => {
    const nestedVast = urlObject.searchParams.get('_vast');
    if (nestedVast) {
        const nestedVastUrlObject = new URL(nestedVast);
        nestedVastUrlObject.searchParams.set('correlator', timestamp);
        nestedVastUrlObject.searchParams.set('description_url', pageUrl);
        nestedVastUrlObject.searchParams.set('url', pageUrl);
        urlObject.searchParams.set('_vast', nestedVastUrlObject.href);
    }
    urlObject.searchParams.set('_api', '2');
    urlObject.searchParams.set('_ssm', '0');
    urlObject.searchParams.set('_tsm', timestamp);
    urlObject.searchParams.set('gdpr', '0');
    urlObject.searchParams.delete('gdpr_consent');
    urlObject.searchParams.delete('_abm');
    urlObject.searchParams.set('_pum', pageUrl);
};
/**
 * Since ad tags are static urls, we need to update them with correct page url where
 * ad tag was used
 */
const updateUrlWithPageInfo = (url) => {
    const urlObject = new URL(url);
    const timestamp = Date.now().toString();
    const pageUrl = location.href || '';
    const isDoubleVerify = urlObject.hostname
        .toLowerCase()
        .includes('doubleverify');
    if (isDoubleVerify) {
        modifyDoubleVerifyUrl(urlObject, timestamp, pageUrl);
    }
    else {
        urlObject.searchParams.set('correlator', timestamp);
        urlObject.searchParams.set('description_url', pageUrl);
        urlObject.searchParams.set('url', pageUrl);
    }
    return urlObject.href;
};
export const GAM_MAX_TRIES = 5;
/**
 * Hits the given GAM URL and returns an XML string.
 *
 * If the XML from GAM gives an empty VAST, we parse the XML for a redirect URL.
 * Then, we request that new GAM url, parse the XML, and look for non-empty VAST again...
 * This happens up to `MAX_TRIES` times.
 */
export function fetchGAM(url, tries = 1) {
    return __awaiter(this, void 0, void 0, function* () {
        const response = yield fetcher({ url: updateUrlWithPageInfo(url) });
        const text = yield response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(text, 'text/xml');
        const isVastValid = Boolean(getFirstElementByTagName(doc, 'VAST') &&
            getFirstElementByTagName(doc, 'AdParameters'));
        if (isVastValid) {
            return text;
        }
        else {
            const redirectUrl = getRedirectUrlFromVastXml(doc);
            return redirectUrl && tries < GAM_MAX_TRIES
                ? yield fetchGAM(redirectUrl, tries + 1)
                : '';
        }
    });
}
